import { useEffect } from "react"
import type { DateTime } from "luxon"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Stack, Typography } from "@mui/material"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import type { DynamicConfigField } from "../../form/formTypes"
import { DynamicConfigKey } from "../../form/formTypes"

import { MeetingTimeFields } from "./MeetingTimeFields"

const useStyles = makeStyles()((theme) => ({
    hint: {
        color: theme.palette.tailwind.slate[500],
    },
}))

type MeetingTimeSectionProps = Readonly<{
    config: DynamicConfigField
}>

export function MeetingTimeSection({ config }: MeetingTimeSectionProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const isLegacyMeetingTime = config.key === DynamicConfigKey.MEETING_TIME

    const [{ value: currentStart }] = useField<DateTime | null>(DynamicConfigKey.MEETING_START_TIME)
    const [, legacyMeta, legacyHelpers] = useField<DateTime | null>(DynamicConfigKey.MEETING_TIME)

    // The legacy single-field key is still validated by the config schema, and yup rejects null even
    // without `required`, so it is only ever overwritten with a real start time.
    useEffect(() => {
        if (!isLegacyMeetingTime || !currentStart) return
        legacyHelpers.setValue(currentStart)
    }, [isLegacyMeetingTime, currentStart?.toISO?.()])

    const requiredMark = config.inputField?.isRequired ? " *" : ""

    return (
        <Stack gap=".5rem">
            <LocalizedStrict id="create-order-meeting-details-duration-helper-text">
                <Typography variant="caption" className={classes.hint}>
                    Enter meeting duration so the cafeteria can improve planning and clearing.
                </Typography>
            </LocalizedStrict>
            <MeetingTimeFields
                startLabel={l10n.getString("create-order-meeting-details-start-time-label") + requiredMark}
                endLabel={l10n.getString("create-order-meeting-details-end-time-label") + requiredMark}
                legacyMeta={isLegacyMeetingTime ? legacyMeta : undefined}
            />
        </Stack>
    )
}
